/**
 * Demo-local event pricing model — a layered rate card that mirrors how FBOs and
 * event organizers actually price a surge like the Masters. Self-contained (no
 * DB): the planner store seeds `DEFAULT_EVENT_PRICING`, the PricingSettingsModal
 * edits it live, and `resolveAircraftRevenue` turns one aircraft + its stay into
 * an itemized dollar breakdown. Pure — mirrors the `engine/capacity.ts` style.
 *
 * Resolution order (see resolveAircraftRevenue):
 *   base rate by tier → event surge → client-group discount → program modifier →
 *   VIP premium → fuel-uplift waiver → per-zone override.
 */
import { type Tier, TIER_ORDER, TIER_LABELS } from '../engine/tiers'
import type { ArrivalInquiry, CatalogEntry, OperatorClass, ProgramMembership } from '../engine/types'
import { tierForCategory } from '../engine/tiers'
import type { FuelRequest } from '../motion/fuelRequest'

export type { Tier }
export { TIER_ORDER, TIER_LABELS }

/** Per-tier base fee matrix (USD). Landing is one-time; the rest are per night. */
export interface TierFeeMatrix {
  /** Landing fee per arrival. */
  landing: Record<Tier, number>
  /** Ramp / parking position fee per night. */
  ramp: Record<Tier, number>
  /** Overnight tie-down surcharge per night. */
  overnight: Record<Tier, number>
  /** Hangar fee per night (billed only for hangar-designated zones). */
  hangar: Record<Tier, number>
}

export interface FuelPricing {
  jetAMarginPerGal: number
  avgasMarginPerGal: number
  /** Gallons above which the high-volume Jet-A margin applies. */
  volumeThresholdGal: number
  /** Jet-A margin for uplift beyond the volume threshold (typically lower). */
  highVolumeMarginPerGal: number
  /** Landing + ramp waived when a single uplift meets/exceeds this many gallons. */
  upliftWaiverGal: number
}

export interface ProgramModifier {
  /** Multiplier on fuel margin (contract fuel < 1). */
  fuelMarginMultiplier: number
  /** Based tenants pay no ramp/overnight. */
  waiveRampOvernight: boolean
  /** Flat fraction off landing/ramp/overnight/hangar (loyalty programs). */
  flatDiscount: number
}

/** The full editable rate card. */
export interface EventPricing {
  fees: TierFeeMatrix
  fuel: FuelPricing
  /** Flat per-occurrence service fees, keyed by service type. */
  services: Record<string, number>
  /** Surge multiplier applied to landing/ramp/overnight/hangar (the Masters premium). */
  surgeMultiplier: number
  /** Flat per-aircraft event handling / slot (PPR) fee. */
  eventHandlingFee: number
  /** Flat premium added per VIP aircraft. */
  vipPremium: number
  /** Fraction off the size-based fees, by operator class (negotiated discount). */
  clientGroupDiscount: Record<OperatorClass, number>
  /** Program / membership modifiers. */
  programModifiers: Record<ProgramMembership, ProgramModifier>
}

/** Per-zone pricing override (set in the zone editor). */
export interface ZonePricingOverride {
  /** Bill this zone as a hangar (hangar fee instead of ramp+overnight). */
  isHangar?: boolean
  rampPerNight?: number | null
  overnightPerNight?: number | null
  hangarPerNight?: number | null
}

/** Itemized revenue for one aircraft over the scoped portion of its stay. */
export interface RevenueBreakdown {
  landing: number
  ramp: number
  overnight: number
  hangar: number
  fuel: number
  services: number
  eventHandling: number
  vip: number
  total: number
}

/** The line items, in display order, with labels. */
export const REVENUE_LINES: Array<{ key: keyof Omit<RevenueBreakdown, 'total'>; label: string }> = [
  { key: 'fuel', label: 'Fuel' },
  { key: 'ramp', label: 'Ramp / Parking' },
  { key: 'overnight', label: 'Overnight' },
  { key: 'hangar', label: 'Hangar' },
  { key: 'landing', label: 'Landing' },
  { key: 'eventHandling', label: 'Event Handling' },
  { key: 'services', label: 'Services' },
  { key: 'vip', label: 'VIP Premium' },
]

export const OPERATOR_CLASS_LABELS: Record<OperatorClass, string> = {
  fractional: 'Fractional',
  management: 'Management Co.',
  individual: 'Individual / Owner',
}

export const PROGRAM_LABELS: Record<ProgramMembership, string> = {
  transient: 'Transient (walk-up)',
  contract_fuel: 'Contract Fuel',
  based_tenant: 'Based Tenant',
  loyalty: 'Loyalty Member',
}

/** Non-fuel service types that carry a flat fee (fuel is priced by gallons). */
export const BILLABLE_SERVICES = ['gpu', 'lav', 'catering', 'rental_car', 'de_ice'] as const

export const SERVICE_LABELS: Record<string, string> = {
  gpu: 'GPU',
  lav: 'Lav Service',
  catering: 'Catering',
  rental_car: 'Rental Car',
  de_ice: 'De-Ice',
  hangar_request: 'Hangar Request',
}

const byTier = (heavy: number, superMid: number, mid: number, light: number, turbo: number, piston: number): Record<Tier, number> => ({
  heavy,
  super_mid: superMid,
  mid,
  light,
  turboprop: turbo,
  piston,
})

/** Seed rate card — a plausible Masters surge schedule (presenter can tune live). */
export const DEFAULT_EVENT_PRICING: EventPricing = {
  fees: {
    landing: byTier(1200, 700, 450, 250, 150, 75),
    ramp: byTier(800, 500, 350, 200, 120, 60),
    overnight: byTier(300, 200, 150, 100, 60, 30),
    hangar: byTier(2500, 1800, 1400, 1000, 700, 400),
  },
  fuel: {
    jetAMarginPerGal: 1.75,
    avgasMarginPerGal: 1.25,
    volumeThresholdGal: 1000,
    highVolumeMarginPerGal: 1.25,
    upliftWaiverGal: 800,
  },
  services: {
    gpu: 150,
    lav: 95,
    catering: 250,
    rental_car: 75,
    de_ice: 600,
  },
  surgeMultiplier: 2.0,
  eventHandlingFee: 350,
  vipPremium: 500,
  clientGroupDiscount: {
    fractional: 0.2,
    management: 0.1,
    individual: 0,
  },
  programModifiers: {
    transient: { fuelMarginMultiplier: 1, waiveRampOvernight: false, flatDiscount: 0 },
    contract_fuel: { fuelMarginMultiplier: 0.55, waiveRampOvernight: false, flatDiscount: 0 },
    based_tenant: { fuelMarginMultiplier: 0.8, waiveRampOvernight: true, flatDiscount: 0 },
    loyalty: { fuelMarginMultiplier: 0.85, waiveRampOvernight: false, flatDiscount: 0.1 },
  },
}

/** Fuel revenue (margin) for an uplift, with a volume tier + program multiplier. */
function fuelRevenue(fuel: FuelRequest, pricing: EventPricing, program: ProgramMembership): number {
  const mod = pricing.programModifiers[program]
  if (fuel.fuelType === 'avgas') {
    return fuel.gallons * pricing.fuel.avgasMarginPerGal * mod.fuelMarginMultiplier
  }
  const { volumeThresholdGal, jetAMarginPerGal, highVolumeMarginPerGal } = pricing.fuel
  const baseGal = Math.min(fuel.gallons, volumeThresholdGal)
  const highGal = Math.max(0, fuel.gallons - volumeThresholdGal)
  const margin = baseGal * jetAMarginPerGal + highGal * highVolumeMarginPerGal
  return margin * mod.fuelMarginMultiplier
}

/** Sum the flat fees for an inquiry's non-fuel services. */
function serviceRevenue(inquiry: ArrivalInquiry, pricing: EventPricing): number {
  let sum = 0
  for (const svc of inquiry.services) {
    if (svc.type === 'fuel' || svc.type.startsWith('fuel_')) continue
    sum += pricing.services[svc.type] ?? 0
  }
  return sum
}

/**
 * Itemized revenue for one aircraft over the scoped portion of its stay.
 *
 * @param opts.nights        nights (of this stay) that fall within the scope.
 * @param opts.includeOneTime whether the one-time charges (landing/fuel/services/
 *        event handling/VIP, charged on arrival) fall within the scope.
 */
export function resolveAircraftRevenue(
  inquiry: ArrivalInquiry,
  entry: CatalogEntry | undefined,
  fuel: FuelRequest | null,
  pricing: EventPricing,
  zoneOverride: ZonePricingOverride | undefined,
  opts: { nights: number; includeOneTime: boolean }
): RevenueBreakdown {
  const tier = tierForCategory(entry?.category)
  const { surgeMultiplier: surge } = pricing
  const mod = pricing.programModifiers[inquiry.program]
  // Combined fraction off the size-based fees: client-group × loyalty flat.
  const sizeDiscount = (1 - pricing.clientGroupDiscount[inquiry.operatorClass]) * (1 - mod.flatDiscount)

  const nights = Math.max(0, opts.nights)
  const isHangar = zoneOverride?.isHangar ?? false

  // Base size-based per-night rates (zone override wins when present).
  const rampRate = zoneOverride?.rampPerNight ?? pricing.fees.ramp[tier]
  const overnightRate = zoneOverride?.overnightPerNight ?? pricing.fees.overnight[tier]
  const hangarRate = zoneOverride?.hangarPerNight ?? pricing.fees.hangar[tier]

  let landing = opts.includeOneTime ? pricing.fees.landing[tier] * surge * sizeDiscount : 0
  let ramp = isHangar ? 0 : rampRate * surge * sizeDiscount * nights
  let overnight = isHangar ? 0 : overnightRate * surge * sizeDiscount * nights
  const hangar = isHangar ? hangarRate * surge * sizeDiscount * nights : 0

  // Based tenants pay no ramp/overnight.
  if (mod.waiveRampOvernight) {
    ramp = 0
    overnight = 0
  }

  // Fuel-uplift waiver: a big enough single uplift waives landing + ramp.
  if (fuel && fuel.gallons >= pricing.fuel.upliftWaiverGal) {
    landing = 0
    ramp = 0
  }

  const fuelRev = opts.includeOneTime && fuel ? fuelRevenue(fuel, pricing, inquiry.program) : 0
  const services = opts.includeOneTime ? serviceRevenue(inquiry, pricing) : 0
  const eventHandling = opts.includeOneTime ? pricing.eventHandlingFee : 0
  const vip = opts.includeOneTime && inquiry.isVip ? pricing.vipPremium : 0

  const total = landing + ramp + overnight + hangar + fuelRev + services + eventHandling + vip
  return { landing, ramp, overnight, hangar, fuel: fuelRev, services, eventHandling, vip, total }
}

/** Add one breakdown into an accumulator (mutates + returns `acc`). */
export function addBreakdown(acc: RevenueBreakdown, b: RevenueBreakdown): RevenueBreakdown {
  acc.landing += b.landing
  acc.ramp += b.ramp
  acc.overnight += b.overnight
  acc.hangar += b.hangar
  acc.fuel += b.fuel
  acc.services += b.services
  acc.eventHandling += b.eventHandling
  acc.vip += b.vip
  acc.total += b.total
  return acc
}

export function emptyBreakdown(): RevenueBreakdown {
  return { landing: 0, ramp: 0, overnight: 0, hangar: 0, fuel: 0, services: 0, eventHandling: 0, vip: 0, total: 0 }
}
